/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { BEM } from "@adapptio/lib-components";
import { useDeepCompareMemo } from "use-deep-compare";
import { ToolbarCommandButton } from "../../../../../ui/components/Toolbar/ToolbarCommandButton";
import { ConfigEditorEdit } from "../../ConfigEditorEdit";
import { ToolbarSpacer } from "../../../../../ui/components/Toolbar/ToolbarSpacer";
import { ToolbarButton } from "../../../../../ui/components/Toolbar/ToolbarButton";
import { useStateWithEvent, useViewInfo } from "../../../../../ui/hooks";
import { useLayoutElement } from "../../../../../ui/components/Layout/useLayoutElement";
import { LayoutState_Column } from "../../../../../ui/components/Layout/LayoutState";

import "./ConfigEditorEditToolbar.scss";

export interface IConfigEditorEditToolbarProps {
	className?: string;
	view: ConfigEditorEdit;
}

export const ConfigEditorEditToolbar: React.FC<IConfigEditorEditToolbarProps> = ({ className, view }) => {
	const baseClass = BEM("edt-config-editor-edit-toolbar");

	const layout = view.getLayout();
	const viewInfo = useViewInfo(view);

	const panelLeft = useLayoutElement<LayoutState_Column>(layout.getById("panelLeft"));

	const saveAllowed = useDeepCompareMemo(() => view.canSave(), [ viewInfo ]);

	const undoRedoEnabled = useStateWithEvent(
		view.getConfigModel().onModelChange,
		() => ({
			undo: view.canUndo(),
			redo: view.canRedo()
		}),
		[ view ]
	);

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<ToolbarButton
				icon="mdi/dock-left"
				active={panelLeft.isExpanded}
				onClick={panelLeft.toggleExpanded}
				tooltip="Toggle Settings Panel"
			/>

			<ToolbarSpacer />

			<ToolbarCommandButton
				icon="mdi/undo-variant"
				commandId="configEditor.edit.undo"
				disabled={!undoRedoEnabled.undo}
				tooltip="Undo"
			/>
			<ToolbarCommandButton
				icon="mdi/redo-variant"
				commandId="configEditor.edit.redo"
				disabled={!undoRedoEnabled.redo}
				tooltip="Redo"
			/>

			<ToolbarSpacer grow={1} />

			<ToolbarCommandButton
				icon="mdi/content-save"
				commandId="configEditor.edit.save"
				disabled={!saveAllowed}
				tooltip="Save"
			/>
		</div>
	);
};
